<template>
  <div class="app-address-search">
    <AppInput
      id="street_address"
      ref="control"
      name="street_address"
      class="app-address-search__input"
      :label="cPostInputText"
      autocomplete="off"
      max-width="lg:max-w-550"
      :icon-right="inputValue.length > 0"
      :error="cPostError"
      :value="inputValue"
      :theme="theme"
      @focus="handleFocus"
      @blur="handleBlur"
      @input="handleInput($event)"
    >
      <div
        v-if="inputValue.length"
        slot="iconRight"
        class="app-address-search__input-close-icon"
        @mousedown="clearInput"
      >
        <FontAwesomeIcon
          icon="xmark"
          class="text-white text-sm"
        />
      </div>
      <template
        v-if="cPostError"
        #error
      >
        <small
          class="text-red-500 font-semibold"
        >
          Search your address again, or
          <u
            class="cursor-pointer text-[#424141]"
            @mousedown="showFallback"
          >
            Click here to type manually</u>
        </small>
      </template>

      <template #dropdown>
        <div
          class="app-address-search__dropdown"
          :class="{hidden: !focused}"
        >
          <div class="app-address-search__dropdown-results">
            <div
              v-for="(address, index) in addresses"
              :key="index"
              class="app-address-search__dropdown-item"
              @mousedown="
                handleAddressRetrieve(address.Id, address.Next === 'Find', index)
              "
            >
              <div class="max-w-[95%] text-sm">
                <strong>
                  {{ address.Text }}
                </strong>
                <span class="font-semibold text-[#818181] italic">{{
                  address.Description
                }}</span>
              </div>
              <div
                v-if="address.Next === 'Find'"
                class="flex flex-row items-center text-purple-500"
              >
                <span
                  class="hidden sm:inline mr-5 ml-10 leading-0 font-semibold"
                >more</span>
                <FontAwesomeIcon
                  icon="chevron-right"
                  class="text-purple-500 text-base"
                />
              </div>
            </div>
          </div>
          <div
            v-if="focused && !isTyping && !addressValue.verified"
            class="app-address-search__dropdown-item app-address-search__dropdown-item--disabled"
          >
            <small class="app-address-search__dropdown-item--instructions">
              Type your Unit, Street, and Partial Street Name
            </small>
          </div>
          <div
            v-if="focused"
            class="app-address-search__dropdown-item border-t-1 border-[#d8d8d8]"
            @mousedown="showFallback"
          >
            <small class="font-semibold text-purple-500">Click here to Type Manually</small>
          </div>
        </div>
      </template>
    </AppInput>

    <div
      v-if="addressValue.verified"
      class="app-address-search__selected"
    >
      <div>
        <span class="text-[#767676] mb-5 inline-block text-sm">The Address you have selected is:</span>
        <br>
        <span class="font-bold max-w-240">
          {{ addressValue.line1 }},
          {{ addressValue.city }}
          {{ addressValue.province }},
          {{ addressValue.postalCode }}
        </span>
      </div>
      <FontAwesomeIcon
        icon="check"
        class="app-address-search__selected-check"
      />
    </div>
  </div>
</template>

<script>
import AppInput from '@/components/common/inputs/AppInput'
import addressSearch from '@/mixins/address-search'
import cpostFields from '../canada-post/cpostFields'
import DataLayer from '@/utilities/data-layer'
import theme from '@/mixins/theme'
import navigation from '@/mixins/navigation'

export default {
  name: 'AppAddressSearch',

  components: {
    AppInput
  },

  mixins: [addressSearch, theme, navigation],

  props: {
    value: {
      type: String,
      default: ''
    },

    label: {
      type: String,
      default: ''
    },

    name: {
      type: String,
      default: ''
    },

    rules: {
      type: Array,
      default: () => []
    },

    theme: {
      type: String,
      default: 'blue'
    },

    timeoutDelay: {
      type: Number,
      default: 750 // 1000ms = 1s
    }
  },

  data () {
    return {
      inputValue: '',
      addresses: [],
      delayActive: false,
      submitting: false,
      focused: false,
      provinces: [
        { code: 'AB', name: 'Alberta' },
        { code: 'BC', name: 'British Columbia' },
        { code: 'MB', name: 'Manitoba' },
        { code: 'NB', name: 'New Brunswick' },
        { code: 'NL', name: 'Newfoundland and Labrador' },
        { code: 'NS', name: 'Nova Scotia' },
        { code: 'ON', name: 'Ontario' },
        { code: 'PE', name: 'Prince Edward Island' },
        { code: 'QC', name: 'Quebec' },
        { code: 'SK', name: 'Saskatchewan' },
        { code: 'NT', name: 'Northwest Territories' },
        { code: 'NU', name: 'Nunavut' },
        { code: 'YT', name: 'Yukon' }
      ]
    }
  },

  computed: {
    locationState () {
      return this.$store.state.location
    },

    isTyping () {
      return this.inputValue.length > 1
    },

    cPostError () {
      return this.$store.state.validations.cPostError
    },

    addressValue () {
      return {
        verified: this.locationState.c_post_verified,
        line1: this.locationState.c_post_Line1,
        line2: this.locationState.c_post_Line2,
        city: this.locationState.c_post_City,
        province: this.locationState.c_post_Province,
        postalCode: this.locationState.c_post_PostalCode,
        fullAddress: this.locationState.c_post_fullAddress
      }
    },

    addressFallback () {
      return this.locationState.addressFallback
    },

    isPersonalLoan () {
      return this.$store.getters['loans/isPersonalLoan']
    },

    cPostInputText () {
      return (
        this.$route.meta.cPostInputText ||
        'Search by Full Address or Street Name'
      )
    }
  },

  watch: {
    delayActive: function (val) {
      if (!val) {
        this.handleAddressSearch()
      }
    },

    $_navigation_triggerContinue: async function (val) {
      this.focused = false
      this.$store.dispatch('setInputFocused', false)
    }
  },

  mounted () {
    this.sendTestRequest()

    if (this.addressValue.fullAddress) {
      const { line1, city, province } = this.addressValue
      this.inputValue = `${line1}, ${city} ${province}`
    }
  },

  methods: {
    handleFocus () {
      this.focused = true
      const eventName = `spring_${this.eventPrefix}_address-search_focus`
      DataLayer.pushObject({ event: eventName })
      if (this.isMobile) {
        this.$_scroll_toElement('body', window, 200)
      }
    },

    handleBlur (e) {
      // if input value is filled, do not blur
      if (this.inputValue.length < 3) this.focused = false
    },

    clearInput () {
      this.inputValue = ''
      this.addresses = []

      // clearup the address fields to prevent showing in manual form
      cpostFields.forEach((item) => {
        this.$store.dispatch(item.vuexAction, '')
      })
      this.$store.dispatch('setCpostVerified', 0)
      this.focused = false
    },

    showFallback () {
      DataLayer.pushEvent('event', 'address_manual')
      this.$store.dispatch('setAddressFallback', true)
    },

    handleInput (value) {
      this.inputValue = value
      this.$store.dispatch('setCpostVerified', 0) // set verified as false if input changes and reset address selected

      if (this.inputValue.length < 3) {
        this.delayActive = false
        this.addresses = []
        return
      }

      if (!this.delayActive && this.inputValue.length > 2) {
        this.delayActive = true

        // Set a new timeout for 1000ms
        this.inputTimeout = setTimeout(() => {
          this.delayActive = false
        }, +this.timeoutDelay)
      }
    },

    /**
     * Send Test Request
     * @desc tests the Cpost to see if it's working
     * If it returns an error, show the fallback
     */
    async sendTestRequest () {
      try {
        this.loading = true
        const response = await this.searchAddress('1', { MaxSuggestions: 1, CDUID: this.$store.state.application.cduid })
        if (!response || response?.Items.length <= 0) {
          this.$store.dispatch('setCPostError', 1)
          this.showFallback()
        }
      } catch (error) {
        console.error(error)
        this.$store.dispatch('setCPostError', 1)
        this.showFallback()
      } finally {
        this.loading = false
      }
    },

    async handleAddressSearch () {
      try {
        const addressSearch = await this.searchAddress(this.inputValue, {
          MaxSuggestions: 7,
          CDUID: this.$store.state.application.cduid
        })

        if (addressSearch.error) {
          throw new Error(addressSearch.error)
        }
        this.focused = true
        this.addresses = addressSearch.Items
      } catch (error) {
        this.$refs['input-validation'].setErrors([addressSearch.error])
        console.error(error)
      }
    },

    async handleAddressRetrieve (ID, searchSubaddresses, index) {
      try {
        if (searchSubaddresses) {
          // if its a subaddress group (units), we need to search again with the addressID
          const search = await this.searchAddress(this.inputValue, {
            LastId: ID,
            CDUID: this.$store.state.application.cduid
          })
          this.addresses = search.Items
          return
        }
        const CanadaPostVersion2 = process.env.VUE_APP_CANADAPOST_VERSION_2 ?? false
        let address

        // CP version 1 uses the find and retrieve canadapost API and compares the results, then sends the retrieve results to the BE
        // CP version 2 only uses the find API
        if (CanadaPostVersion2) {
          address = await this.extractAddressInfo(this.addresses[index], ID)
        } else {
          // if its a single address or a selection from subaddress group
          address = await this.getAddressViaRetrieveApi(ID)
          const findAddressResults = await this.extractAddressInfo(this.addresses[index], ID)
          this.$store.dispatch('setCpostVerified', 1)
          this.compareAddressInfo(findAddressResults, address)
        }

        // depending on env variable, either use the address info retrieved from the free CP API, or the paid CP API
        this.populateAddress(address)
        this.focused = false
      } catch (error) {
        this.$refs['input-validation'].setErrors([error.message])
        console.error(error)
        this.$store.dispatch('setCpostVerified', 0)
        // unfocus input to close dropdown
        this.focused = false
      }
    },

    /**
     * extracts address info from retrieve CP API
     * @param ID
     */
    async getAddressViaRetrieveApi (ID) {
      // if its a single address or a selection from subaddress group
      const retrieve = await this.retrieveAddress(ID, { CDUID: this.$store.state.application.cduid })
      if (retrieve.error || retrieve?.Items.length < 1) throw new Error(retrieve.error)

      // filter items where Language is 'ENG'
      return retrieve.Items.find((item) => item.Language === 'ENG')
    },

    /**
     * extracts address info from free CP API
     * @param address
     * @param ID
     */
    async extractAddressInfo (address, ID) {
      try {
        const [City, ProvinceCode, PostalCode] = address.Description.split(', ')
        const possibleBuildingNumber = address.Text.split(' ')[0] ?? ''
        const streetAddress = address.Text.split(' ').slice(1).join(' ') ?? ''
        // Some addresses have the unity number. E.g. 102-30445
        const possibleUnitNumberWithBuildingNumber = possibleBuildingNumber.match(/[-\d]+/)?.[0] ?? ''
        const splittedNumber = possibleUnitNumberWithBuildingNumber.split('-')
        const buildingNumber = splittedNumber[splittedNumber.length - 1]
        const provinceName = this.provinces.find((province) => province.code === ProvinceCode)?.name ?? ''
        if (provinceName === 'Quebec') {
          return await this.getAddressViaRetrieveApi(ID)
        } else {
          return {
            BuildingName: '',
            BuildingNumber: buildingNumber,
            City,
            Company: '',
            CountryName: 'Canada',
            District: '',
            FormattedLine1: address.Text,
            Label: '',
            Line1: address.Text,
            Line2: '',
            Line3: '',
            Line4: '',
            Line5: '',
            PostalCode,
            Province: ProvinceCode,
            ProvinceCode,
            ProvinceName: provinceName,
            Street: streetAddress,
            SubBuilding: '',
            verified: ''
          }
        }
      } catch (e) {
        console.error(e.response)
      }
    },

    /**
     * Compares data retrieved from free CP API to see if the necessary
     * fields for credit pull match the results from the paid CP API
     * @param findAddressInfo
     * @param retrieveAddressInfo
     */
    compareAddressInfo (findAddressInfo, retrieveAddressInfo) {
      let flag = false
      const addressMismatchArray = []
      const creditPullAddressFields = [
        'Street',
        'PostalCode',
        'Province',
        'City'
      ]

      creditPullAddressFields.forEach(field => {
        const findValue = findAddressInfo[field]
        const retrieveValue = retrieveAddressInfo[field]
        if (findValue !== retrieveValue) {
          addressMismatchArray.push(field)
          flag = true
        }
      })

      if (flag) DataLayer.pushObject({ event: 'spring_ploan_address_mismatch', addressMismatchArray })
      else DataLayer.pushObject({ event: 'spring_ploan_address_match' })
    },

    /**
     * Populate the address fields
     * @emits populate
     */
    populateAddress (address) {
      address.verified = 1
      address.error = 0
      cpostFields.forEach((item) => {
        this.$store.dispatch(item.vuexAction, address[item.key])
      })

      const fullAddressString = `${address.Line1}, ${address.City} ${address.Province}`
      this.inputValue = fullAddressString
      this.$store.dispatch('setStreetAddress', address.Line1)

      let event = 'address_selected'
      if (this.isPersonalLoan) event = 'spring_ploan_address_selected'
      DataLayer.pushEvent('event', event)
    }
  }
}
</script>

<style lang="scss" scoped>
.app-address-search {
  @apply mx-auto max-w-500 lg:max-w-400 relative;

  :deep() .app-input__label {
    @apply text-sm;
  }

  &__input {
    @apply mb-0;

    &-close-icon {
      @apply bg-[#6B7280] rounded-full text-white cursor-pointer w-[17px] h-[17px] flex items-center justify-center duration-500 hover:bg-purple-500;
    }
  }

  &__dropdown {
    @apply bg-white shadow-dropdown rounded-b-10 absolute w-full z-20;

    &-results {
      @apply overflow-y-auto max-h-300 border-b-1 border-[#D8D8D8];
    }

    &-item {
      @apply cursor-pointer px-15 py-12 text-md hover:bg-gray-200;
      @apply flex items-center justify-between;

      &--disabled {
        //remove hover effect on disabled item
        @apply hover:bg-white cursor-default;
      }

      &--instructions {
        @apply text-[#848484] font-semibold text-sm;
      }
    }
  }

  &__selected {
    @apply border-2 border-purple-100 text-center bg-white relative font-semibold;
    @apply mx-auto py-15 px-40 mt-25 relative;

    &-check {
      @apply text-[#02B776] text-xl w-15 sm:w-25;
      @apply absolute right-[20px] top-1/2 transform -translate-y-1/2;
    }

    //speech box arrow on top of the box
    &::after {
      @apply absolute top-[-13px] left-[10px] w-20 h-20;
      @apply bg-white border-l-2 border-t-2 border-purple-100;

      content: "";
      transform: rotate(45deg) skew(15deg, 15deg);
    }
  }
}
</style>
